export type DaySchedule = {
  open: boolean;
  intervals: Array<{ from: string; to: string }>;
};

export type WeeklyHours = {
  monday: DaySchedule;
  tuesday: DaySchedule;
  wednesday: DaySchedule;
  thursday: DaySchedule;
  friday: DaySchedule;
  saturday: DaySchedule;
  sunday: DaySchedule;
};

export type EditableProfileFields = {
  tagline: string | null;
  description: string | null;
  email: string | null;
  phone: string | null;
  website: string | null;
  instagram_handle: string | null;
  facebook_handle: string | null;
  tiktok_handle: string | null;
  address: string | null;
  operating_hours: WeeklyHours | null;
  price_range: string | null;
  languages: string[] | null;
  business_name: string;
  latitude: number | null;
  longitude: number | null;
};

export const PRICE_RANGES = ['€', '€€', '€€€', '€€€€'] as const;

export const COMMON_LANGUAGES = [
  'English', 'Italian', 'Spanish', 'French', 'German',
  'Portuguese', 'Dutch', 'Polish', 'Russian', 'Mandarin',
  'Japanese', 'Korean',
] as const;

export const TAGLINE_MAX = 80;

export class DirtyTracker<T extends Record<string, unknown>> {
  private original: T;
  private current: T;

  constructor(initial: T) {
    this.original = structuredClone(initial);
    this.current = structuredClone(initial);
  }

  set<K extends keyof T>(key: K, value: T[K]): void {
    this.current[key] = value;
  }

  get<K extends keyof T>(key: K): T[K] {
    return this.current[key];
  }

  getAll(): T {
    return structuredClone(this.current);
  }

  isDirty(): boolean {
    return JSON.stringify(this.original) !== JSON.stringify(this.current);
  }

  diff(): Partial<T> {
    const out: Partial<T> = {};
    for (const k of Object.keys(this.current) as Array<keyof T>) {
      if (JSON.stringify(this.current[k]) !== JSON.stringify(this.original[k])) {
        out[k] = this.current[k];
      }
    }
    return out;
  }

  reset(newOriginal: T): void {
    this.original = structuredClone(newOriginal);
    this.current = structuredClone(newOriginal);
  }
}

const DAY_KEYS = [
  'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday',
] as const satisfies ReadonlyArray<keyof WeeklyHours>;

export function isWeeklyHoursShape(raw: unknown): raw is WeeklyHours {
  if (!raw || typeof raw !== 'object') return false;
  const obj = raw as Record<string, unknown>;
  for (const day of DAY_KEYS) {
    const v = obj[day];
    if (!v || typeof v !== 'object') return false;
    const d = v as Record<string, unknown>;
    if (typeof d.open !== 'boolean') return false;
    if (!Array.isArray(d.intervals)) return false;
  }
  return true;
}

export function normalizeWeeklyHours(raw: unknown): WeeklyHours | null {
  if (!raw || typeof raw !== 'object') return null;
  const src = raw as Record<string, unknown>;
  const out = {} as WeeklyHours;
  for (const day of DAY_KEYS) {
    const v = src[day];
    if (v && typeof v === 'object') {
      const d = v as Record<string, unknown>;
      const intervalsRaw = Array.isArray(d.intervals) ? d.intervals : [];
      const intervals: Array<{ from: string; to: string }> = [];
      for (const iv of intervalsRaw) {
        if (iv && typeof iv === 'object') {
          const o = iv as Record<string, unknown>;
          if (typeof o.from === 'string' && typeof o.to === 'string') {
            intervals.push({ from: o.from, to: o.to });
          }
        }
      }
      out[day] = { open: d.open === true, intervals };
    } else {
      out[day] = { open: false, intervals: [] };
    }
  }
  return out;
}

// ── App-compatible serialization ─────────────────────────────────────────────
// The PlanIt / Curate Flutter apps AND the in-app provider editor read+write
// `operating_hours` (jsonb) as a FLAT map:
//   { "Monday": "9:00 AM - 6:00 PM", ..., "Sunday": "Closed" }
// — TitleCase English day names, 12-hour display strings, literal "Closed" for
// closed days. (Verified against the live column: every populated row uses this
// shape; nothing uses the structured {open,intervals} shape, and the apps' parser
// stringifies any nested object into garbage.) The picker keeps the structured
// WeeklyHours shape for good editing UX, but we SERIALIZE to the flat shape on
// save and PARSE it back on load, so web-entered hours actually render in the
// apps and stay consistent with the in-app editor writing the same column.

const DAY_LABELS: Record<keyof WeeklyHours, string> = {
  monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday',
  friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday',
};

/** "09:00" -> "9:00 AM", "18:00" -> "6:00 PM", "00:00" -> "12:00 AM". */
function to12h(hhmm: string): string {
  const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
  if (!m) return hhmm;
  let h = Number(m[1]);
  const min = m[2];
  const period = h < 12 ? 'AM' : 'PM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${min} ${period}`;
}

/** "9:00 AM" / "6:00 PM" / "6 PM" / already-24h "18:00" -> "HH:MM" (or null). */
function to24h(label: string): string | null {
  const ampm = /^(\d{1,2})(?::(\d{2}))?\s*([AaPp])[Mm]$/.exec(label.trim());
  if (ampm) {
    let h = Number(ampm[1] ?? '0');
    const min = ampm[2] ?? '00';
    const pm = (ampm[3] ?? '').toLowerCase() === 'p';
    if (h === 12) h = 0;
    if (pm) h += 12;
    if (h > 23) return null;
    return `${String(h).padStart(2, '0')}:${min}`;
  }
  const h24 = /^(\d{1,2}):(\d{2})$/.exec(label.trim());
  if (h24) {
    const hh = h24[1] ?? '';
    const mm = h24[2] ?? '';
    if (hh && Number(hh) <= 23) return `${hh.padStart(2, '0')}:${mm}`;
  }
  return null;
}

/** WeeklyHours -> flat { "Monday": "9:00 AM - 6:00 PM" | "Closed", ... } (app shape). */
export function weeklyHoursToDisplayMap(hours: WeeklyHours): Record<string, string> {
  const out: Record<string, string> = {};
  for (const day of DAY_KEYS) {
    const sched = hours[day];
    out[DAY_LABELS[day]] =
      !sched.open || sched.intervals.length === 0
        ? 'Closed'
        : sched.intervals.map((iv) => `${to12h(iv.from)} - ${to12h(iv.to)}`).join(', ');
  }
  return out;
}

function parseDaySchedule(v: unknown): DaySchedule {
  // Legacy structured shape { open, intervals:[{from,to}] } — tolerated on load.
  if (v && typeof v === 'object' && !Array.isArray(v)) {
    const o = v as Record<string, unknown>;
    if ('open' in o || 'intervals' in o) {
      const intervals: Array<{ from: string; to: string }> = [];
      for (const iv of Array.isArray(o.intervals) ? o.intervals : []) {
        if (iv && typeof iv === 'object') {
          const p = iv as Record<string, unknown>;
          if (typeof p.from === 'string' && typeof p.to === 'string') {
            intervals.push({ from: p.from, to: p.to });
          }
        }
      }
      return { open: o.open === true, intervals };
    }
  }
  // Flat display string: "9:00 AM - 6:00 PM" (possibly comma-separated) | "Closed".
  if (typeof v === 'string') {
    const s = v.trim();
    if (s === '' || s.toLowerCase() === 'closed') return { open: false, intervals: [] };
    const intervals: Array<{ from: string; to: string }> = [];
    for (const part of s.split(',')) {
      const seg = part.split(/\s*[-–—]\s*/);
      if (seg.length >= 2) {
        const from = to24h(seg[0] ?? '');
        const to = to24h(seg[seg.length - 1] ?? '');
        if (from && to) intervals.push({ from, to });
      }
    }
    return { open: intervals.length > 0, intervals };
  }
  return { open: false, intervals: [] };
}

/**
 * Parse stored `operating_hours` into WeeklyHours for the picker. Accepts the
 * flat app shape (TitleCase keys + display strings), any-case keys, and the
 * legacy structured shape. Unrecognised days fall back to closed.
 */
export function parseOperatingHours(raw: unknown): WeeklyHours | null {
  if (!raw || typeof raw !== 'object') return null;
  const src = raw as Record<string, unknown>;
  const byLower = new Map<string, unknown>();
  for (const k of Object.keys(src)) byLower.set(k.toLowerCase(), src[k]);
  const out = {} as WeeklyHours;
  for (const day of DAY_KEYS) out[day] = parseDaySchedule(byLower.get(day));
  return out;
}

export { DAY_KEYS };
